import { QueryClientProvider } from '@tanstack/react-query'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import Layout from '@/components/common/Layout'
import AchievementDetailPage from '@/pages/AchievementDetailPage'
import AchievementsPage from '@/pages/AchievementsPage'
import TrophyPage from '@/pages/TrophyPage'
import StatusPage from '@/pages/StatusPage'
import FriendsPage from '@/pages/FriendsPage'
import FriendSearchPage from '@/pages/FriendSearchPage'
import FriendProfilePage from '@/pages/FriendProfilePage'
import TaskListPage from '@/pages/TaskListPage'
import LoginPage from '@/pages/LoginPage'
import RegisterPage from '@/pages/RegisterPage'
import SettingsPage from '@/pages/SettingsPage'
import ChangePasswordPage from '@/pages/ChangePasswordPage'
import SetPasswordPage from '@/pages/SetPasswordPage'
import { queryClient } from '@/lib/queryClient'
import { saveRedirect } from '@/lib/redirectStorage'
import { useEffect, type ReactNode } from 'react'

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  const from = location.pathname + location.search

  // 元のURL（クエリ含む）を保持し、ログイン後にそのページへ戻す。ProtectedRoute は
  // 全保護ルート共通なので、ここ1か所で全ページに効く。state はSPA内遷移用、
  // sessionStorage はハードリロード・OAuthリダイレクト・別ブラウザでの開き直し
  // （LINE内ブラウザ→外部ブラウザ等）で state が失われても残す保険。
  // 保存は副作用なので effect で行う（レンダーの純粋性を保つ）。SPA内の同期遷移は
  // state が確実に渡るため、sessionStorage 書き込みの順序に依存しない。
  useEffect(() => {
    if (!isLoading && !isAuthenticated) saveRedirect(from)
  }, [isLoading, isAuthenticated, from])

  if (isLoading) return null
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from }} />
  return <>{children}</>
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<TaskListPage />} />
        <Route path="achievements" element={<AchievementsPage />} />
        <Route path="achievements/:id" element={<AchievementDetailPage />} />
        <Route path="trophies" element={<TrophyPage />} />
        <Route path="status" element={<StatusPage />} />
        <Route path="friends/search" element={<FriendSearchPage />} />
        <Route path="friends/invite/:token" element={<FriendProfilePage />} />
        <Route path="friends/:id" element={<FriendProfilePage />} />
        <Route path="friends" element={<FriendsPage />} />
      </Route>
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/set-password"
        element={
          <ProtectedRoute>
            <SetPasswordPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ''}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Toaster
              position="bottom-left"
              richColors
              duration={2000}
              offset="calc(env(safe-area-inset-bottom) + 56px)"
              mobileOffset={{ bottom: 'calc(env(safe-area-inset-bottom) + 56px)', left: '8px' }}
            />
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  )
}
